import React, { useState, useEffect, useRef } from 'react';
import { Trophy, Timer, Plus, Trash2, Gift } from 'lucide-react';
import type { GameStats, Reward } from '../types';

interface RewardsPanelProps {
  stats: GameStats;
  onRewardAdd: (reward: Reward) => void;
  onRewardRemove: (points: number) => void;
  onTimeSet: (minutes: number) => void;
  onReset: () => void;
}

export function RewardsPanel({ stats, onRewardAdd, onRewardRemove, onTimeSet, onReset }: RewardsPanelProps) {
  const [newRewardPoints, setNewRewardPoints] = useState('');
  const [newRewardDescription, setNewRewardDescription] = useState('');
  const [timeLimit, setTimeLimit] = useState('');
  const [timeDisplay, setTimeDisplay] = useState<{ minutes: number; seconds: number } | null>(null);
  const [reachedReward, setReachedReward] = useState<Reward | null>(null);
  const [sessionPoints, setSessionPoints] = useState(0);
  const animationFrameRef = useRef<number>();
  const previousPoints = useRef(stats.points);

  // Reset session points when timer starts
  useEffect(() => {
    if (stats.timeStart) {
      setSessionPoints(0);
    }
  }, [stats.timeStart]);

  // Update session points when total points change
  useEffect(() => {
    const pointsDiff = stats.points - previousPoints.current;
    if (pointsDiff > 0 && stats.timeStart) {
      setSessionPoints(prev => prev + pointsDiff);
    }
    previousPoints.current = stats.points;
  }, [stats.points, stats.timeStart]);

  const handleRewardAdd = (e: React.FormEvent) => {
    e.preventDefault();
    const points = parseInt(newRewardPoints);
    if (points > 0 && newRewardDescription.trim()) {
      onRewardAdd({
        points,
        description: newRewardDescription.trim()
      });
      setNewRewardPoints('');
      setNewRewardDescription('');
    }
  };

  const handleTimeSet = (e: React.FormEvent) => {
    e.preventDefault();
    const minutes = parseInt(timeLimit);
    if (minutes > 0) {
      setSessionPoints(0); // Reset only session points
      onTimeSet(minutes);
      setTimeLimit('');
    }
  };

  const updateTimeDisplay = () => {
    if (!stats.timeLimit || !stats.timeStart) {
      setTimeDisplay(null);
      return false;
    }

    const now = Date.now();
    const endTime = stats.timeStart + stats.timeLimit * 60 * 1000;
    const remaining = Math.max(0, endTime - now);
    
    if (remaining === 0) {
      return false;
    }

    const minutes = Math.floor(remaining / (1000 * 60));
    const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
    
    setTimeDisplay({ minutes, seconds });
    return true;
  };

  useEffect(() => {
    const animate = () => {
      if (updateTimeDisplay()) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    if (stats.timeLimit && stats.timeStart) {
      animationFrameRef.current = requestAnimationFrame(animate);
    } else {
      setTimeDisplay(null);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [stats.timeLimit, stats.timeStart]);

  // Sprawdzanie osiągnięcia progu nagrody
  useEffect(() => {
    const currentPoints = stats.points;
    const previousPointsValue = previousPoints.current;
    
    // Znajdź pierwszą nagrodę, której próg został przekroczony
    const reachedReward = stats.rewards
      .filter(reward => 
        previousPointsValue < reward.points && 
        currentPoints >= reward.points
      )
      .sort((a, b) => a.points - b.points)[0];

    if (reachedReward) {
      setReachedReward(reachedReward);
      // Odtwórz dźwięk sukcesu
      try {
        const audio = new Audio('/sounds/correct.mp3');
        audio.play();
      } catch (error) {
        console.warn('Could not play sound:', error);
      }
      // Ukryj powiadomienie po 5 sekundach
      setTimeout(() => setReachedReward(null), 5000);
    }
  }, [stats.points, stats.rewards]);

  // Oblicz postęp do następnej nagrody
  const getNextReward = () => {
    const nextReward = stats.rewards
      .filter(reward => reward.points > stats.points)
      .sort((a, b) => a.points - b.points)[0];
    
    if (nextReward) {
      const progress = (stats.points / nextReward.points) * 100;
      return {
        reward: nextReward,
        progress: Math.min(progress, 100)
      };
    }
    return null;
  };

  const nextRewardInfo = getNextReward();

  return (
    <div className="bg-white p-4 rounded-lg shadow-md space-y-4">
      <h2 className="text-xl font-bold flex items-center">
        <Trophy className="w-6 h-6 mr-2 flex-shrink-0" />
        System nagród
      </h2>

      <div className="p-3 bg-blue-50 rounded-lg">
        <div className="text-base sm:text-lg font-semibold mb-2 break-words">
          Punkty: {stats.points}
          {stats.timeStart && (
            <span className="text-sm text-gray-600 block sm:inline sm:ml-2">
              (W tej sesji: {sessionPoints})
            </span>
          )}
        </div>
        <div className="text-xs sm:text-sm text-gray-600">
          <div>Łatwy poziom: 1 punkt</div>
          <div>Średni poziom: 2 punkty</div>
          <div>Trudny poziom: 3 punkty</div>
        </div>
      </div>

      {reachedReward && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center font-bold text-yellow-800 animate-bounce">
          <div className="flex items-center justify-center gap-2">
            <Gift className="w-6 h-6" />
            <span>Brawo! Zdobyłeś nagrodę: {reachedReward.description}!</span>
          </div>
        </div>
      )}

      {nextRewardInfo && (
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="text-sm font-semibold mb-2">
            Następna nagroda: {nextRewardInfo.reward.description} ({nextRewardInfo.reward.points} pkt)
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-blue-500 h-2.5 rounded-full transition-all duration-500"
              style={{ width: `${nextRewardInfo.progress}%` }}
            />
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Pozostało: {nextRewardInfo.reward.points - stats.points} punktów
          </div>
        </div>
      )}

      {timeDisplay && (
        <div className="p-4 bg-green-50 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="font-semibold">Pozostały czas:</span>
            <span className="text-xl font-bold">
              {String(timeDisplay.minutes).padStart(2, '0')}:
              {String(timeDisplay.seconds).padStart(2, '0')}
            </span>
          </div>
        </div>
      )}

      <form onSubmit={handleTimeSet} className="space-y-2">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1 flex items-center gap-2">
            <Timer className="w-5 h-5 text-gray-500 flex-shrink-0" />
            <input
              type="number"
              value={timeLimit}
              onChange={(e) => setTimeLimit(e.target.value)}
              placeholder="Ustaw czas (minuty)"
              className="w-full p-2 border rounded-md"
              min="1"
              disabled={timeDisplay !== null}
            />
          </div>
          <button
            type="submit"
            className={`px-4 py-2 rounded-md ${
              timeDisplay !== null
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-500 hover:bg-green-600'
            } text-white whitespace-nowrap`}
            disabled={timeDisplay !== null}
          >
            {timeDisplay !== null ? 'Licznik aktywny' : 'Rozpocznij'}
          </button>
        </div>
      </form>

      <div className="space-y-2">
        <h3 className="font-semibold">Nagrody:</h3>
        {stats.rewards.map((reward) => (
          <div
            key={reward.points}
            className={`flex items-center justify-between p-2 rounded-md ${
              stats.points >= reward.points ? 'bg-green-50' : 'bg-gray-50'
            }`}
          >
            <div className="flex-1 min-w-0">
              <span className="font-semibold">{reward.points} pkt</span>
              <span className="mx-2">-</span>
              <span className="break-words">{reward.description}</span>
            </div>
            <button
              onClick={() => onRewardRemove(reward.points)}
              className="text-red-500 hover:text-red-700 ml-2 flex-shrink-0"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <form onSubmit={handleRewardAdd} className="space-y-2">
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <div className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-gray-500 flex-shrink-0" />
              <input
                type="number"
                value={newRewardPoints}
                onChange={(e) => setNewRewardPoints(e.target.value)}
                placeholder="Punkty"
                className="w-20 p-2 border rounded-md"
                min="1"
              />
            </div>
            <input
              type="text"
              value={newRewardDescription}
              onChange={(e) => setNewRewardDescription(e.target.value)}
              placeholder="Opis nagrody"
              className="flex-1 p-2 border rounded-md min-w-0"
            />
          </div>
          <button
            type="submit"
            className="w-full bg-blue-500 text-white p-2 rounded-md hover:bg-blue-600"
          >
            Dodaj
          </button>
        </div>
      </form>
    </div>
  );
}